/**
 * Compliance > CoAs. Grid of supplier Certificates of Analysis filed by PO.
 *
 * One row per certificate-per-PO; an unmatched certificate shows once with no
 * PO (the work queue). Click a row to review the PDF side-by-side with the
 * editable fields. Mirrors the Commerce Orders list.
 */

import { useCallback, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Download, Loader2, ExternalLink } from 'lucide-react';
import { ToolPinToggle } from '@/components/ToolPinToggle';
import { Pill } from '@/components/Pill';
import { SearchableSelect } from '@/components/SearchableSelect';
import { ClearableSearchInput } from '@/components/ClearableSearchInput';
import { InfoTooltip, SortableHeader, gridTheadClassName, type SortDir } from '@ampersandbrands/ui';
import { DataGridShell } from '@/components/grid/DataGridShell';
import { CoasTabs } from './CoasTabs';
import { useSession } from '@/lib/useSession';
import { formatDateTime } from '@/lib/dateUtils';
import {
  capitalizeFirst,
  downloadCoasXlsx,
  docTypeLabel,
  hasComplianceCoaRead,
  statusLabel,
  useCoaFilters,
  useCoas,
  type CoaFilters,
  type CoaRow,
} from '@/lib/useCoas';

const STATUS_TONES: Record<string, string> = {
  unmatched: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
  filed: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
  missing: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
  not_missing: 'bg-gray-100 text-gray-600 dark:bg-gray-800/60 dark:text-gray-300',
};

function StatusPill({ status }: { status: string }) {
  const tone = STATUS_TONES[status] ?? 'bg-gray-100 text-gray-600 dark:bg-gray-800/60 dark:text-gray-300';
  return <Pill tone={tone}>{statusLabel(status)}</Pill>;
}

function readArray(sp: URLSearchParams, key: string): string[] {
  const v = sp.get(key);
  return v ? v.split(',').filter(Boolean) : [];
}

// Sortable columns for the CoAs grid. Unlike Orders (which sorts server-side
// over a cursor list), the CoAs backend does not accept a sort param, so the
// sort runs client-side over the rows already loaded. The infinite-scroll
// sentinel keeps loading pages in the backend's default order; the chosen
// sort reorders whatever rows are currently in hand.
type CoaSortColumn =
  | 'received_at'
  | 'po_number'
  | 'item_number'
  | 'supplier'
  | 'manufacturer'
  | 'file_name'
  | 'doc_type'
  | 'status'
  | 'confidence';

// Rank confidence so high > medium > none/null in a sort rather than sorting
// the raw strings alphabetically (which would put 'high' before 'medium').
const CONFIDENCE_RANK: Record<string, number> = { high: 3, medium: 2, low: 1, none: 0 };

function coaSortValue(row: CoaRow, col: CoaSortColumn): string | number {
  switch (col) {
    case 'received_at': return row.received_at ?? '';
    case 'po_number': return row.po_number ?? '';
    case 'item_number': return row.item_number ?? '';
    case 'supplier': return (row.sender_name ?? row.sender_email ?? '').toLowerCase();
    case 'manufacturer': return (row.manufacturer ?? '').toLowerCase();
    case 'file_name': return row.file_name.toLowerCase();
    case 'doc_type': return docTypeLabel(row.doc_type).toLowerCase();
    case 'status': return statusLabel(row.status).toLowerCase();
    case 'confidence': return CONFIDENCE_RANK[row.confidence ?? 'none'] ?? -1;
  }
}

function sortCoaRows(rows: CoaRow[], col: CoaSortColumn, dir: SortDir): CoaRow[] {
  const factor = dir === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    const av = coaSortValue(a, col);
    const bv = coaSortValue(b, col);
    if (av < bv) return -1 * factor;
    if (av > bv) return 1 * factor;
    return 0;
  });
}

export function CoAsList() {
  const { user } = useSession();
  const canView = hasComplianceCoaRead(user?.permissions, user?.superAdmin);

  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get('q') ?? '';
  const status = searchParams.get('status') ?? '';
  const docType = readArray(searchParams, 'doc_type');
  const supplier = searchParams.get('supplier') ?? '';

  const update = useCallback(
    (mutate: (sp: URLSearchParams) => void) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        mutate(next);
        return next;
      }, { replace: true });
    },
    [setSearchParams],
  );
  const setStr = (k: string, v: string) => update((sp) => (v ? sp.set(k, v) : sp.delete(k)));
  const setArr = (k: string, v: string[]) => update((sp) => (v.length ? sp.set(k, v.join(',')) : sp.delete(k)));
  const resetFilters = () => update((sp) => ['q', 'status', 'doc_type', 'supplier'].forEach((k) => sp.delete(k)));

  const filters: CoaFilters = useMemo(
    () => ({
      q: q || undefined,
      status: status || undefined,
      doc_type: docType.length ? docType : undefined,
      supplier: supplier || undefined,
    }),
    [q, status, docType, supplier],
  );

  const coasQ = useCoas(filters);
  const filtersQ = useCoaFilters();
  const [exporting, setExporting] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  // Client-side sort state. Tri-state cycle per column: asc -> desc -> off.
  const [sort, setSort] = useState<{ column: CoaSortColumn; dir: SortDir } | null>(null);
  const toggleSort = (col: CoaSortColumn) => {
    setSort((prev) => {
      if (!prev || prev.column !== col) return { column: col, dir: 'asc' };
      if (prev.dir === 'asc') return { column: col, dir: 'desc' };
      return null;
    });
  };
  const sortedFor = (col: CoaSortColumn): SortDir | null =>
    sort && sort.column === col ? sort.dir : null;

  if (user && !canView) return <AccessDeniedCard />;

  const rows = sort ? sortCoaRows(coasQ.rows, sort.column, sort.dir) : coasQ.rows;

  const onExport = async () => {
    setExporting(true);
    setToast(null);
    try {
      await downloadCoasXlsx(filters);
      setToast('Export downloaded.');
    } catch (err) {
      setToast(err instanceof Error ? err.message : String(err));
    } finally {
      setExporting(false);
    }
  };

  const filterBar = (
    <div className="flex flex-wrap gap-3 items-end">
      <div className="flex-1 min-w-[220px]">
        <label htmlFor="search" className="block text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted mb-1">Search</label>
        <ClearableSearchInput
          id="search"
          value={q}
          onChange={(v) => setStr('q', v)}
          placeholder="PO, item #, supplier, manufacturer, file name..."
          className="w-full border border-amp-line rounded pl-3 py-1.5 text-sm bg-amp-white text-amp-ink placeholder:text-amp-ink-muted dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink dark:placeholder:text-amp-dark-ink-muted"
        />
      </div>
      <div>
        <label htmlFor="status" className="block text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted mb-1">Status</label>
        <select
          id="status"
          value={status}
          onChange={(e) => setStr('status', e.target.value)}
          className="min-w-[140px] border border-amp-line rounded px-3 py-1.5 text-sm bg-amp-white text-amp-ink dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink"
        >
          <option value="">All</option>
          {(filtersQ.options?.statuses ?? []).map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>
      <div>
        <span className="inline-flex items-baseline text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted mb-1">
          Document type
        </span>
        <SearchableSelect
          ariaLabel="Document type filter"
          options={filtersQ.options?.doc_types ?? []}
          selected={docType}
          onChange={(v) => setArr('doc_type', v)}
          multiSelect
          placeholder="Any"
        />
      </div>
      <div>
        <span className="inline-flex items-baseline text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted mb-1">
          Supplier
          <InfoTooltip label="Supplier">Search by supplier name. The list can be long, so this filter supports text search.</InfoTooltip>
        </span>
        <SearchableSelect
          ariaLabel="Supplier filter"
          options={filtersQ.options?.suppliers ?? []}
          selected={supplier ? [supplier] : []}
          onChange={(v) => setStr('supplier', v[0] ?? '')}
          placeholder="Any"
        />
      </div>
      {(q || status || docType.length || supplier) && (
        <button
          type="button"
          onClick={resetFilters}
          className="text-xs border border-amp-line rounded px-3 py-1.5 text-amp-ink hover:bg-amp-cream-warm dark:border-amp-dark-line dark:text-amp-dark-ink dark:hover:bg-amp-dark-surface-warm"
        >
          Reset filters
        </button>
      )}
    </div>
  );

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex items-start justify-between mb-6 gap-4">
        <div className="min-w-0 flex-1">
          <p className="text-xs uppercase tracking-wide text-amp-olive dark:text-amp-olive-dark mb-1">Compliance</p>
          <h1 className="text-2xl font-semibold text-amp-ink dark:text-amp-dark-ink">CoAs</h1>
          <p className="text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">
            Supplier Certificates of Analysis, filed by PO. Click a row to review the PDF and fields side by side.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <ToolPinToggle toolSlug="coas" toolName="CoAs" />
          <button
            type="button"
            onClick={onExport}
            disabled={exporting}
            className="inline-flex items-center gap-1 text-xs border border-amp-line rounded px-3 py-1.5 text-amp-ink hover:bg-amp-cream-warm disabled:cursor-not-allowed disabled:opacity-60 dark:border-amp-dark-line dark:text-amp-dark-ink dark:hover:bg-amp-dark-surface-warm"
          >
            {exporting ? <Loader2 className="h-3 w-3 animate-spin" /> : <Download className="h-3 w-3" />}
            Export to Excel
          </button>
        </div>
      </div>

      <CoasTabs />

      {toast && (
        <div className="mb-4 rounded-md border border-amp-line bg-amp-cream-warm px-3 py-2 text-sm text-amp-ink dark:border-amp-dark-line dark:bg-amp-dark-surface-warm dark:text-amp-dark-ink" role="status">
          {toast}
        </div>
      )}

      <DataGridShell
        total={coasQ.totalCount}
        loaded={rows.length}
        countNoun="certificate rows"
        toolbar={filterBar}
        onReachEnd={coasQ.fetchNextPage}
        hasMore={coasQ.hasNextPage}
        isLoadingMore={coasQ.isFetchingNextPage}
      >
        <table className="min-w-full text-sm">
          <thead className={gridTheadClassName}>
            <tr>
              <SortableHeader
                label="Date Received"
                sorted={sortedFor('received_at')}
                onToggle={() => toggleSort('received_at')}
                thClassName="px-3 py-2"
              />
              <SortableHeader
                label="PO #"
                sorted={sortedFor('po_number')}
                onToggle={() => toggleSort('po_number')}
                thClassName="px-3 py-2"
              />
              <SortableHeader
                label="Item #"
                sorted={sortedFor('item_number')}
                onToggle={() => toggleSort('item_number')}
                thClassName="px-3 py-2"
              />
              <SortableHeader
                label="Supplier"
                sorted={sortedFor('supplier')}
                onToggle={() => toggleSort('supplier')}
                thClassName="px-3 py-2"
              />
              <SortableHeader
                label="Manufacturer"
                sorted={sortedFor('manufacturer')}
                onToggle={() => toggleSort('manufacturer')}
                thClassName="px-3 py-2"
              />
              <SortableHeader
                label="File"
                sorted={sortedFor('file_name')}
                onToggle={() => toggleSort('file_name')}
                thClassName="px-3 py-2"
              />
              <SortableHeader
                label="Type"
                sorted={sortedFor('doc_type')}
                onToggle={() => toggleSort('doc_type')}
                thClassName="px-3 py-2"
              />
              <SortableHeader
                label="Status"
                sorted={sortedFor('status')}
                onToggle={() => toggleSort('status')}
                thClassName="px-3 py-2"
              />
              <SortableHeader
                label="Confidence"
                sorted={sortedFor('confidence')}
                onToggle={() => toggleSort('confidence')}
                thClassName="px-3 py-2"
              />
            </tr>
          </thead>
          <tbody className="divide-y divide-amp-line dark:divide-amp-dark-line">
            {coasQ.isLoading ? (
              <tr><td colSpan={9} className="px-4 py-6 text-amp-ink-muted dark:text-amp-dark-ink-muted">Loading certificates...</td></tr>
            ) : coasQ.error ? (
              <tr><td colSpan={9} className="px-4 py-6 text-red-700 dark:text-red-300">Could not load: {coasQ.error.message}</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={9} className="px-4 py-6 text-amp-ink-muted dark:text-amp-dark-ink-muted">No certificates match this filter.</td></tr>
            ) : (
              rows.map((row) => <CoaListRow key={`${row.document_id}:${row.po_id ?? 'none'}`} row={row} />)
            )}
          </tbody>
        </table>
      </DataGridShell>
    </div>
  );
}

function CoaListRow({ row }: { row: CoaRow }) {
  const detailHref = {
    pathname: `/compliance/coas/${row.document_id}`,
    search: typeof window !== 'undefined' ? window.location.search : '',
  };
  return (
    <tr className="hover:bg-amp-cream-warm dark:hover:bg-amp-dark-surface-warm">
      <td className="px-3 py-2 text-amp-ink-muted dark:text-amp-dark-ink-muted whitespace-nowrap">
        {formatDateTime(row.received_at)}
      </td>
      <td className="px-3 py-2 font-mono text-xs text-amp-ink dark:text-amp-dark-ink">{row.po_number ?? '--'}</td>
      <td className="px-3 py-2 font-mono text-xs text-amp-ink dark:text-amp-dark-ink">
        {row.item_number ? (
          <a
            href={`/commerce/products/${row.item_number}`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="hover:underline text-amp-olive dark:text-amp-olive-dark"
          >
            {row.item_number}
          </a>
        ) : '--'}
      </td>
      <td className="px-3 py-2 text-amp-ink dark:text-amp-dark-ink">{row.sender_name ?? row.sender_email ?? '--'}</td>
      <td className="px-3 py-2 text-amp-ink dark:text-amp-dark-ink">{row.manufacturer ?? '--'}</td>
      <td className="px-3 py-2 text-amp-ink dark:text-amp-dark-ink">
        <div className="flex items-center gap-1">
          <Link to={detailHref} className="hover:underline">{row.file_name}</Link>
          {row.sharepoint_web_url && (
            <a
              href={row.sharepoint_web_url}
              target="_blank"
              rel="noopener noreferrer"
              title="Open in SharePoint"
              onClick={(e) => e.stopPropagation()}
              className="text-amp-ink-muted hover:text-amp-ink dark:text-amp-dark-ink-muted dark:hover:text-amp-dark-ink"
            >
              <ExternalLink className="h-3 w-3" aria-hidden />
            </a>
          )}
        </div>
      </td>
      <td className="px-3 py-2 text-amp-ink-muted dark:text-amp-dark-ink-muted">{docTypeLabel(row.doc_type)}</td>
      <td className="px-3 py-2"><StatusPill status={row.status} /></td>
      <td className="px-3 py-2 text-amp-ink-muted dark:text-amp-dark-ink-muted">{capitalizeFirst(row.confidence)}</td>
    </tr>
  );
}

function AccessDeniedCard() {
  return (
    <div className="max-w-xl rounded-lg border border-amp-line p-5 dark:border-amp-dark-line">
      <h2 className="text-lg font-semibold text-amp-ink dark:text-amp-dark-ink mb-1">No access</h2>
      <p className="text-sm text-amp-ink dark:text-amp-dark-ink">
        CoAs is gated by the compliance.coas access slug. Ask IT to grant you access.
      </p>
    </div>
  );
}
